/**
 * 事件总线：引擎内各模块的唯一通信通道。
 * 系统之间不直接互相引用，只通过事件解耦。
 */
export class EventBus {
  constructor() {
    this._handlers = new Map();
  }

  /** 订阅事件，返回取消订阅函数 */
  on(type, fn) {
    if (!this._handlers.has(type)) this._handlers.set(type, new Set());
    this._handlers.get(type).add(fn);
    return () => this.off(type, fn);
  }

  once(type, fn) {
    const off = this.on(type, (payload) => {
      off();
      fn(payload);
    });
    return off;
  }

  off(type, fn) {
    const set = this._handlers.get(type);
    if (set) set.delete(fn);
  }

  emit(type, payload) {
    const set = this._handlers.get(type);
    if (set) {
      for (const fn of [...set]) {
        try {
          fn(payload);
        } catch (err) {
          console.error(`[EventBus] 处理事件 ${type} 出错`, err);
        }
      }
    }
    // 全局通配监听，便于调试
    const any = this._handlers.get('*');
    if (any) for (const fn of [...any]) fn({ type, payload });
  }

  clear(type) {
    if (type) this._handlers.delete(type);
    else this._handlers.clear();
  }
}

/** 引擎内建事件名常量，避免拼错 */
export const EV = {
  GAME_READY: 'game:ready',
  GAME_PAUSE: 'game:pause',
  SCENE_ENTER: 'scene:enter',
  SCENE_LEAVE: 'scene:leave',
  PLAYER_MOVE: 'player:move',
  INTERACT_NEAR: 'interact:near',
  INTERACT_LEAVE: 'interact:leave',
  INTERACT_DO: 'interact:do',
  // 姿态：坐、躺、起身、起跳
  POSE_SIT: 'pose:sit',
  POSE_UP: 'pose:up',
  PLAYER_JUMP: 'player:jump',
  PLAYER_LAND: 'player:land',
  INVENTORY_ADD: 'inventory:add',
  INVENTORY_REMOVE: 'inventory:remove',
  TIME_TICK: 'time:tick',
  TIME_NEWDAY: 'time:newday',
  DB_SAVED: 'db:saved',
  DB_LOADED: 'db:loaded',
  TOAST: 'ui:toast',
  DIALOG: 'ui:dialog',
  // 联机与电视
  NET_STATUS: 'net:status',
  NET_PEERS: 'net:peers',
  // 对讲机：录音状态（rec/send/idle）与收到的一条语音
  VOICE_STATE: 'voice:state',
  VOICE_IN: 'voice:in',
  TV_OPEN: 'tv:open',
  TV_SYNC: 'tv:sync',
  SHELF_OPEN: 'shelf:open',
  NOTES_OPEN: 'notes:open',
  // 城里的几块面板：商店、告示榜、证照、地块、雇工、舆图
  SHOP_OPEN: 'shop:open',
  BOARD_OPEN: 'board:open',
  LICENSE_OPEN: 'license:open',
  STALL_OPEN: 'stall:open',
  HIRE_OPEN: 'hire:open',
  MAP_OPEN: 'map:open',
  // 攒车：工作台 / 车铺工位上的那块面板
  GARAGE_OPEN: 'garage:open',
  // 灵感：冒出一个念头（fragment）/ 忽然明白一件事得了资格证（cert）/ 修行上了一个境界（realm）
  INSIGHT: 'insight:got',
};
